import { useCallback, useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import {
  Package, Plus, Trash2, CheckCircle2, XCircle, ArrowLeftCircle,
  AlertTriangle, Clock, Search, X, ShieldCheck, RotateCw, Printer, ShoppingBag,
} from "lucide-react";
import Layout from "../components/Layout";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Staff Item Borrowing for Live — admin issues stock to a staff member
 * for a live session, tracks it through Approve → Return → Approve, and
 * flags any missing items as OUTSTANDING until physically resolved.
 *
 * Two-eye control by design: creation is one admin action, approval is a
 * separate admin action. Same admin can do both (small shop), but the
 * record + audit log always shows them as two events.
 */

const TABS = [
  { key: "active",      label: "Active",      icon: Clock },
  { key: "outstanding", label: "Outstanding", icon: AlertTriangle },
  { key: "history",     label: "History",     icon: Package },
];

// Human labels + colours for the workflow status badge.
const STATUS_META = {
  pending_issue_approval:  { label: "Pending Approval", cls: "bg-amber-500/10 text-amber-300 border-amber-400/30" },
  issued:                  { label: "With Staff",       cls: "bg-blue-500/10 text-blue-300 border-blue-400/30" },
  pending_return_approval: { label: "Return · Verify",  cls: "bg-purple-500/10 text-purple-300 border-purple-400/30" },
  outstanding:             { label: "Outstanding",      cls: "bg-red-500/10 text-red-300 border-red-400/40" },
  closed:                  { label: "Returned",         cls: "bg-emerald-500/10 text-emerald-300 border-emerald-400/30" },
  cancelled:               { label: "Cancelled",        cls: "bg-white/5 text-[#8A8A8A] border-white/10" },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function Borrowings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("active");
  const [rows, setRows] = useState([]);
  const [outstanding, setOutstanding] = useState({ records: [], missing_items: [] });
  const [creating, setCreating] = useState(false);
  const [returning, setReturning] = useState(null);   // borrowing being returned
  const [detail, setDetail] = useState(null);        // read-only detail modal
  const [staffFilter, setStaffFilter] = useState("");
  const [skuFilter, setSkuFilter] = useState("");

  const canCreate = user?.role === "admin";
  const canApprove = user?.role === "admin";

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (tab !== "active") params.set("status", tab);
    if (staffFilter.trim()) params.set("staff_name", staffFilter.trim());
    if (skuFilter.trim()) params.set("sku", skuFilter.trim());
    if (tab === "active") params.set("status", "active");
    api.get(`/borrowings?${params.toString()}`)
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"));

    if (tab === "outstanding") {
      api.get("/borrowings/outstanding")
        .then((r) => setOutstanding(r.data))
        .catch(() => {});
    }
  }, [tab, staffFilter, skuFilter]);

  useEffect(() => { load(); }, [load]);

  const approveIssue = async (id) => {
    try {
      await api.post(`/borrowings/${id}/approve-issue`);
      toast.success("Issue approved — items are now with staff");
      load();
      // Auto-open the handover slip so the admin can print + hand it to
      // the staff right away — matches the "issue → print → hand over"
      // workflow the shop follows on live day.
      setTimeout(() => printSlip(id), 400);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const approveReturn = async (id) => {
    try {
      const { data } = await api.post(`/borrowings/${id}/approve-return`);
      if (data.status === "outstanding") toast.warning("Return approved · some items OUTSTANDING");
      else toast.success("Return approved — all items back in stock");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this borrowing request?")) return;
    try {
      await api.post(`/borrowings/${id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Open the PDF handover slip inline (auth-fetched blob → new tab). The
  // browser's built-in PDF viewer gives the admin an instant Ctrl+P.
  const printSlip = async (id) => {
    try {
      const token = localStorage.getItem("gv_token");
      const res = await fetch(`${API}/borrowings/${id}/slip.pdf`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to load slip");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) window.location.href = url;  // popup blocked → fall back
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message || "Failed to open slip");
    }
  };

  return (
    <Layout
      title="Staff Item Borrowing"
      subtitle="Live-session lending · issue → return → verify"
      action={canCreate && (
        <button
          className="btn-gold px-5 py-2.5 text-xs uppercase tracking-wider flex items-center gap-2"
          onClick={() => setCreating(true)}
          data-testid="new-borrowing-btn"
        >
          <Plus className="w-4 h-4" /> New Borrowing
        </button>
      )}
    >
      <div className="flex flex-wrap gap-1 border-b border-white/10 mb-6 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`borrow-tab-${t.key}`}
              className={`px-4 py-3 text-xs mono uppercase tracking-wider inline-flex items-center gap-2 whitespace-nowrap border-b-2 transition-colors ${
                active ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#8A8A8A] border-transparent hover:text-[#EAEAEA]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
          <input
            className="luxe-input pl-9 pr-3 py-2 text-sm w-56"
            placeholder="Filter by staff name"
            value={staffFilter}
            onChange={(e) => setStaffFilter(e.target.value)}
            data-testid="filter-staff"
          />
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
          <input
            className="luxe-input pl-9 pr-3 py-2 text-sm w-56"
            placeholder="Filter by SKU"
            value={skuFilter}
            onChange={(e) => setSkuFilter(e.target.value)}
            data-testid="filter-sku"
          />
        </div>
        <button
          onClick={load}
          className="text-xs mono uppercase tracking-wider text-[#8A8A8A] hover:text-[#D4AF37] flex items-center gap-1.5 px-3"
          data-testid="refresh-btn"
        >
          <RotateCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {tab === "outstanding" && (
        <OutstandingSummary missing={outstanding.missing_items} />
      )}

      <BorrowingsTable
        rows={rows}
        onOpen={setDetail}
        onApproveIssue={canApprove ? approveIssue : null}
        onCancel={canApprove ? cancel : null}
        onReturn={(r) => setReturning(r)}
        onApproveReturn={canApprove ? approveReturn : null}
        onPrintSlip={printSlip}
      />

      {creating && (
        <NewBorrowingModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
      {returning && (
        <ReturnModal
          borrowing={returning}
          onClose={() => setReturning(null)}
          onSubmitted={() => { setReturning(null); load(); }}
        />
      )}
      {detail && (
        <DetailModal borrowing={detail} onClose={() => setDetail(null)} />
      )}
      <Toaster position="top-center" theme="dark" />
    </Layout>
  );
}

// =====================================================================
// TABLE
// =====================================================================

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-white/5" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[0.6rem] mono uppercase tracking-wider border ${m.cls}`}>
      {m.label}
    </span>
  );
}

function BorrowingsTable({ rows, onOpen, onApproveIssue, onCancel, onReturn, onApproveReturn, onPrintSlip }) {
  if (!rows.length) {
    return (
      <div className="glass-card p-12 text-center text-[#666] text-sm">
        No borrowing records here yet.
      </div>
    );
  }
  return (
    <div className="glass-card overflow-x-auto" data-testid="borrowings-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10 text-[#8A8A8A]">
            <th className="text-left px-4 py-3 text-[0.6rem] mono uppercase tracking-wider">Date</th>
            <th className="text-left px-4 py-3 text-[0.6rem] mono uppercase tracking-wider">Staff</th>
            <th className="text-left px-4 py-3 text-[0.6rem] mono uppercase tracking-wider">Live Session</th>
            <th className="text-left px-4 py-3 text-[0.6rem] mono uppercase tracking-wider">Items</th>
            <th className="text-left px-4 py-3 text-[0.6rem] mono uppercase tracking-wider">Status</th>
            <th className="text-right px-4 py-3 text-[0.6rem] mono uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]"
                data-testid={`borrow-row-${r.id}`}>
              <td className="px-4 py-3 text-[#EAEAEA] mono text-xs">{fmtDate(r.borrow_date)}</td>
              <td className="px-4 py-3">
                <div className="text-[#EAEAEA]">{r.staff_name}</div>
                <div className="text-[0.6rem] text-[#666] mono">by {r.created_by_name}</div>
              </td>
              <td className="px-4 py-3 text-[#8A8A8A]">{r.live_session || <span className="text-[#444]">—</span>}</td>
              <td className="px-4 py-3">
                <button
                  onClick={() => onOpen(r)}
                  className="text-[#D4AF37] hover:text-white text-xs underline underline-offset-2"
                  data-testid={`open-borrow-${r.id}`}
                >
                  {r.items.length} item{r.items.length !== 1 ? "s" : ""}
                </button>
              </td>
              <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5 justify-end">
                  {r.status === "pending_issue_approval" && onApproveIssue && (
                    <button onClick={() => onApproveIssue(r.id)}
                            className="text-[0.6rem] mono uppercase tracking-wider px-2 py-1 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`approve-issue-${r.id}`}>
                      <ShieldCheck className="w-3 h-3 inline mr-1" /> Approve Issue
                    </button>
                  )}
                  {r.status === "pending_issue_approval" && onCancel && (
                    <button onClick={() => onCancel(r.id)}
                            className="text-[0.6rem] mono uppercase tracking-wider px-2 py-1 border border-white/10 text-[#8A8A8A] hover:text-red-300 hover:border-red-400/40">
                      Cancel
                    </button>
                  )}
                  {(r.status === "issued" || r.status === "outstanding") && (
                    <button onClick={() => onReturn(r)}
                            className="text-[0.6rem] mono uppercase tracking-wider px-2 py-1 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                            data-testid={`return-btn-${r.id}`}>
                      <ArrowLeftCircle className="w-3 h-3 inline mr-1" /> Return Items
                    </button>
                  )}
                  {r.status === "pending_return_approval" && onApproveReturn && (
                    <button onClick={() => onApproveReturn(r.id)}
                            className="text-[0.6rem] mono uppercase tracking-wider px-2 py-1 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`approve-return-${r.id}`}>
                      <ShieldCheck className="w-3 h-3 inline mr-1" /> Approve Return
                    </button>
                  )}
                  {r.status !== "pending_issue_approval" && r.status !== "cancelled" && onPrintSlip && (
                    <button onClick={() => onPrintSlip(r.id)}
                            title="Print handover slip for staff"
                            className="text-[0.6rem] mono uppercase tracking-wider px-2 py-1 border border-white/10 text-[#8A8A8A] hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
                            data-testid={`print-slip-${r.id}`}>
                      <Printer className="w-3 h-3 inline mr-1" /> Slip
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// OUTSTANDING TAB — per-item breakdown
// =====================================================================

function OutstandingSummary({ missing }) {
  if (!missing?.length) {
    return (
      <div className="glass-card p-6 mb-4 flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-400" />
        <div>
          <div className="text-sm">No outstanding items — every issued piece is accounted for.</div>
        </div>
      </div>
    );
  }
  return (
    <div className="glass-card p-4 mb-4 border-l-2 border-red-400/60" data-testid="outstanding-summary">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-red-300" />
        <div className="text-sm text-red-200 mono uppercase tracking-wider text-[0.7rem]">
          {missing.length} outstanding item{missing.length !== 1 ? "s" : ""}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[#8A8A8A]">
            <tr className="border-b border-white/5">
              <th className="text-left py-2 pr-4 mono uppercase text-[0.55rem]">SKU</th>
              <th className="text-left py-2 pr-4 mono uppercase text-[0.55rem]">Item</th>
              <th className="text-left py-2 pr-4 mono uppercase text-[0.55rem]">Staff</th>
              <th className="text-left py-2 pr-4 mono uppercase text-[0.55rem]">Live Session</th>
              <th className="text-right py-2 mono uppercase text-[0.55rem]">Missing</th>
            </tr>
          </thead>
          <tbody>
            {missing.map((m, i) => (
              <tr key={i} className="border-b border-white/5">
                <td className="py-2 pr-4 mono text-[#D4AF37]">{m.sku}</td>
                <td className="py-2 pr-4 text-[#EAEAEA]">{m.name}</td>
                <td className="py-2 pr-4">{m.staff_name}</td>
                <td className="py-2 pr-4 text-[#8A8A8A]">{m.live_session || "—"}</td>
                <td className="py-2 text-right mono text-red-300">{m.quantity_missing}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// NEW BORROWING MODAL
// =====================================================================

function NewBorrowingModal({ onClose, onCreated }) {
  const [staffName, setStaffName] = useState("");
  const [staffOptions, setStaffOptions] = useState([]);
  const [liveSession, setLiveSession] = useState("");
  const [borrowDate, setBorrowDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([{ product_id: "", quantity: 1, condition_before: "excellent", notes: "" }]);
  const [products, setProducts] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    // Grab any staff/admin user names as suggestions — the field is free
    // text so the operator can also type a name that isn't a portal user.
    api.get("/auth/users").then((r) => {
      setStaffOptions((r.data || []).filter((u) => ["staff", "sales", "admin"].includes(u.role)));
    }).catch(() => {});
  }, []);

  const addItem = () => setItems((p) => [...p, { product_id: "", quantity: 1, condition_before: "excellent", notes: "" }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const updateItem = (i, patch) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const submit = async () => {
    if (!staffName.trim()) return toast.error("Staff name is required");
    if (items.some((it) => !it.product_id)) return toast.error("Pick a product for every row");
    setBusy(true);
    try {
      await api.post("/borrowings", {
        staff_name: staffName.trim(),
        live_session: liveSession.trim(),
        borrow_date: borrowDate,
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: Number(it.quantity) || 1,
          condition_before: it.condition_before,
          notes: it.notes,
        })),
        notes,
      });
      toast.success("Borrowing created — awaiting Approve Issue");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="New Borrowing · Issue Items to Staff">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Staff Name">
            <input
              list="staff-names"
              className="luxe-input w-full px-3 py-2 text-sm"
              value={staffName}
              onChange={(e) => setStaffName(e.target.value)}
              placeholder="e.g. Anna, Bella…"
              data-testid="new-borrow-staff"
            />
            <datalist id="staff-names">
              {staffOptions.map((s) => <option key={s.id} value={s.name} />)}
            </datalist>
          </Field>
          <Field label="Date">
            <input
              type="date"
              className="luxe-input w-full px-3 py-2 text-sm mono"
              value={borrowDate}
              onChange={(e) => setBorrowDate(e.target.value)}
              data-testid="new-borrow-date"
            />
          </Field>
          <Field label="Live Session">
            <input
              className="luxe-input w-full px-3 py-2 text-sm"
              value={liveSession}
              onChange={(e) => setLiveSession(e.target.value)}
              placeholder="e.g. TikTok Live 10 Feb"
              data-testid="new-borrow-session"
            />
          </Field>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-[0.55rem]">Items to issue</div>
            <button onClick={addItem}
                    className="text-[0.6rem] mono uppercase tracking-wider text-[#D4AF37] hover:text-white flex items-center gap-1"
                    data-testid="add-item-row">
              <Plus className="w-3 h-3" /> Add item
            </button>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <ItemRow
                key={i} index={i} item={it} products={products}
                onChange={(patch) => updateItem(i, patch)}
                onRemove={items.length > 1 ? () => removeItem(i) : null}
              />
            ))}
          </div>
        </div>

        <Field label="Notes (optional)">
          <textarea
            className="luxe-input w-full px-3 py-2 text-sm"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="new-borrow-notes"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-xs mono uppercase tracking-wider text-[#8A8A8A] hover:text-white">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="btn-gold px-6 py-2 text-xs uppercase tracking-wider disabled:opacity-50"
                  data-testid="submit-new-borrow">
            {busy ? "Creating…" : "Submit for Approval"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ItemRow({ index, item, products, onChange, onRemove }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return products.slice(0, 20);
    return products
      .filter((p) => (p.name?.toLowerCase().includes(query) || p.sku?.toLowerCase().includes(query)))
      .slice(0, 20);
  }, [q, products]);
  const chosen = products.find((p) => p.id === item.product_id);
  const available = chosen ? (Number(chosen.stock || 0) - Number(chosen.borrowed_stock || 0)) : 0;

  return (
    <div className="grid grid-cols-12 gap-2 items-start bg-black/20 border border-white/5 p-2" data-testid={`item-row-${index}`}>
      <div className="col-span-12 md:col-span-5">
        {!chosen ? (
          <>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search SKU or name…"
              className="luxe-input w-full px-2 py-1.5 text-xs"
              data-testid={`item-search-${index}`}
            />
            <div className="max-h-32 overflow-y-auto mt-1 border border-white/5">
              {filtered.map((p) => {
                const avail = Number(p.stock || 0) - Number(p.borrowed_stock || 0);
                return (
                  <button
                    key={p.id}
                    onClick={() => onChange({ product_id: p.id })}
                    disabled={avail <= 0}
                    className="w-full text-left px-2 py-1 text-xs hover:bg-[#D4AF37]/10 disabled:opacity-30 flex justify-between"
                    data-testid={`pick-${p.sku}`}
                  >
                    <span><span className="mono text-[#D4AF37]">{p.sku}</span> · {p.name}</span>
                    <span className="text-[0.6rem] text-[#8A8A8A] mono">{avail} avail</span>
                  </button>
                );
              })}
              {!filtered.length && <div className="px-2 py-2 text-[0.65rem] text-[#666]">No matches</div>}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-xs">
            <div className="flex-1 min-w-0">
              <div className="mono text-[#D4AF37]">{chosen.sku}</div>
              <div className="text-[#8A8A8A] truncate">{chosen.name}</div>
              <div className="text-[0.6rem] mono text-[#666]">available: {available}</div>
            </div>
            <button onClick={() => onChange({ product_id: "" })}
                    className="text-[#666] hover:text-red-300">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="col-span-4 md:col-span-2">
        <div className="overline text-[0.5rem] mb-0.5">Qty</div>
        <input type="number" min="1" step="1"
               value={item.quantity}
               onChange={(e) => onChange({ quantity: Math.max(1, Number(e.target.value) || 1) })}
               className="luxe-input w-full px-2 py-1.5 text-sm mono"
               data-testid={`item-qty-${index}`} />
      </div>
      <div className="col-span-8 md:col-span-3">
        <div className="overline text-[0.5rem] mb-0.5">Condition Before</div>
        <select value={item.condition_before}
                onChange={(e) => onChange({ condition_before: e.target.value })}
                className="luxe-input w-full px-2 py-1.5 text-sm">
          <option value="excellent">Excellent</option>
          <option value="good">Good</option>
          <option value="fair">Fair</option>
          <option value="minor scratch">Minor scratch</option>
          <option value="damaged">Damaged</option>
        </select>
      </div>
      <div className="col-span-12 md:col-span-2">
        <div className="overline text-[0.5rem] mb-0.5">Notes</div>
        <div className="flex gap-1">
          <input value={item.notes}
                 onChange={(e) => onChange({ notes: e.target.value })}
                 className="luxe-input flex-1 min-w-0 px-2 py-1.5 text-xs"
                 placeholder="—" />
          {onRemove && (
            <button onClick={onRemove}
                    className="px-2 text-[#666] hover:text-red-300"
                    data-testid={`remove-item-${index}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// RETURN MODAL
// =====================================================================

function ReturnModal({ borrowing, onClose, onSubmitted }) {
  const [items, setItems] = useState(() => borrowing.items.map((it) => {
    // Load prior disposition if the operator is re-submitting a return.
    const disp = it.disposition && it.disposition !== "pending"
      ? it.disposition
      : (it.returned ? "returned" : "returned");
    return {
      product_id: it.product_id,
      sku: it.sku,
      name: it.name,
      quantity: it.quantity,
      disposition: disp,
      quantity_returned: it.quantity_returned || it.quantity,
      quantity_sold: it.quantity_sold || it.quantity,
      condition_after: it.condition_after || "excellent",
      notes: it.return_notes || "",
      // Sale-only fields — sale_price is a STRING so the input can be
      // empty ("") without React collapsing it to a stuck leading 0. We
      // parse to a number only at submit time.
      sale_price: it.sale_price ? String(Math.round(it.sale_price)) : "",
      sale_customer_name: it.sale_customer_name || "",
      sale_customer_phone: it.sale_customer_phone || "",
      sale_payment_method: it.sale_payment_method || "Cash",
    };
  }));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (i, patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const submit = async () => {
    // Validation: sold items need a positive price. sale_price is a STRING
    // in local state (see initial-value note above) so parse before checking.
    for (const it of items) {
      const priceNum = Number(it.sale_price) || 0;
      if (it.disposition === "sold" && priceNum <= 0) {
        return toast.error(`Sale price required for ${it.sku}`);
      }
    }
    setBusy(true);
    try {
      await api.post(`/borrowings/${borrowing.id}/return`, {
        items: items.map((it) => {
          const base = {
            product_id: it.product_id,
            disposition: it.disposition,
            condition_after: it.condition_after,
            notes: it.notes,
          };
          if (it.disposition === "sold") {
            return {
              ...base,
              quantity_sold: Number(it.quantity_sold) || 0,
              sale_price: Number(it.sale_price) || 0,
              sale_customer_name: it.sale_customer_name,
              sale_customer_phone: it.sale_customer_phone,
              sale_payment_method: it.sale_payment_method,
            };
          }
          if (it.disposition === "returned") {
            return { ...base, quantity_returned: Number(it.quantity_returned) || 0 };
          }
          return base;  // lost — no extra fields
        }),
        notes,
      });
      toast.success("Return submitted — awaiting Approve Return");
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={`Return Items · ${borrowing.staff_name}`}>
      <div className="space-y-4">
        <div className="text-xs text-[#8A8A8A]">
          For each piece: was it <span className="text-emerald-300">returned</span>,
          <span className="text-[#D4AF37]"> sold during live</span>, or
          <span className="text-red-300"> missing</span>? Sold pieces create a
          sale invoice automatically and leave inventory. Admin verifies before
          finalising.
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <ReturnRow key={i} index={i} item={it}
                       onChange={(patch) => update(i, patch)} />
          ))}
        </div>
        <Field label="Overall return notes (optional)">
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="luxe-input w-full px-3 py-2 text-sm" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-xs mono uppercase tracking-wider text-[#8A8A8A] hover:text-white">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="btn-gold px-6 py-2 text-xs uppercase tracking-wider disabled:opacity-50"
                  data-testid="submit-return">
            {busy ? "Submitting…" : "Submit Return"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ReturnRow({ index, item, onChange }) {
  const disp = item.disposition;
  const isReturned = disp === "returned";
  const isSold = disp === "sold";
  const isLost = disp === "lost";
  return (
    <div className="bg-black/20 border border-white/5 p-3 space-y-2" data-testid={`return-row-${index}`}>
      <div className="grid grid-cols-12 gap-2 items-center">
        <div className="col-span-12 md:col-span-4">
          <div className="mono text-[#D4AF37] text-xs">{item.sku}</div>
          <div className="text-sm">{item.name}</div>
          <div className="text-[0.6rem] mono text-[#666]">issued: {item.quantity}</div>
        </div>
        <div className="col-span-12 md:col-span-8">
          <div className="overline text-[0.5rem] mb-1">Disposition</div>
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => onChange({ disposition: "returned" })}
              className={`px-3 py-1.5 text-[0.6rem] mono uppercase border ${isReturned ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-300" : "border-white/10 text-[#666]"}`}
              data-testid={`disp-returned-${index}`}
            >
              <CheckCircle2 className="w-3 h-3 inline mr-1" /> Returned
            </button>
            <button
              onClick={() => onChange({ disposition: "sold" })}
              className={`px-3 py-1.5 text-[0.6rem] mono uppercase border ${isSold ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-[#666]"}`}
              data-testid={`disp-sold-${index}`}
            >
              <ShoppingBag className="w-3 h-3 inline mr-1" /> Sold During Live
            </button>
            <button
              onClick={() => onChange({ disposition: "lost" })}
              className={`px-3 py-1.5 text-[0.6rem] mono uppercase border ${isLost ? "border-red-400/50 bg-red-500/10 text-red-300" : "border-white/10 text-[#666]"}`}
              data-testid={`disp-lost-${index}`}
            >
              <XCircle className="w-3 h-3 inline mr-1" /> Missing
            </button>
          </div>
        </div>
      </div>

      {isReturned && (
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-4 md:col-span-3">
            <div className="overline text-[0.5rem] mb-0.5">Qty back</div>
            <input type="number" min="0" max={item.quantity} step="1"
                   value={item.quantity_returned}
                   onChange={(e) => onChange({ quantity_returned: Number(e.target.value) })}
                   className="luxe-input w-full px-2 py-1.5 text-sm mono"
                   data-testid={`qty-back-${index}`} />
          </div>
          <div className="col-span-8 md:col-span-4">
            <div className="overline text-[0.5rem] mb-0.5">Condition After</div>
            <select value={item.condition_after}
                    onChange={(e) => onChange({ condition_after: e.target.value })}
                    className="luxe-input w-full px-2 py-1.5 text-sm">
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="minor scratch">Minor scratch</option>
              <option value="damaged">Damaged</option>
            </select>
          </div>
          <div className="col-span-12 md:col-span-5">
            <div className="overline text-[0.5rem] mb-0.5">Notes</div>
            <input value={item.notes}
                   onChange={(e) => onChange({ notes: e.target.value })}
                   className="luxe-input w-full px-2 py-1.5 text-xs"
                   placeholder="—" />
          </div>
        </div>
      )}

      {isSold && (
        <div className="grid grid-cols-12 gap-2 bg-[#D4AF37]/[0.03] border border-[#D4AF37]/20 p-2">
          <div className="col-span-6 md:col-span-2">
            <div className="overline text-[0.5rem] mb-0.5">Qty sold</div>
            <input type="number" min="1" max={item.quantity} step="1"
                   value={item.quantity_sold}
                   onChange={(e) => onChange({ quantity_sold: Number(e.target.value) })}
                   className="luxe-input w-full px-2 py-1.5 text-sm mono"
                   data-testid={`qty-sold-${index}`} />
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="overline text-[0.5rem] mb-0.5">Price / piece (IDR)</div>
            {/* Custom numeric input — plain `<input type="number">` on mobile
                gets stuck on the leading "0" from an initial numeric zero.
                We store the raw digit string in state and strip anything
                non-numeric so the user can clear/edit freely. Parsed to a
                Number only at submit time. */}
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={item.sale_price}
              onChange={(e) => {
                // Keep only digits; drop leading zeros so "01000000" → "1000000".
                const clean = e.target.value.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
                onChange({ sale_price: clean });
              }}
              className="luxe-input w-full px-2 py-1.5 text-sm mono"
              placeholder="e.g. 350000"
              data-testid={`sale-price-${index}`}
            />
            {item.sale_price && (
              <div className="text-[0.55rem] mono text-[#D4AF37] mt-0.5">
                Rp {new Intl.NumberFormat("id-ID").format(Number(item.sale_price) || 0)}
              </div>
            )}
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="overline text-[0.5rem] mb-0.5">Customer name</div>
            <input value={item.sale_customer_name}
                   onChange={(e) => onChange({ sale_customer_name: e.target.value })}
                   className="luxe-input w-full px-2 py-1.5 text-xs"
                   placeholder="walk-in ok"
                   data-testid={`sale-customer-${index}`} />
          </div>
          <div className="col-span-6 md:col-span-2">
            <div className="overline text-[0.5rem] mb-0.5">Phone</div>
            <input value={item.sale_customer_phone}
                   onChange={(e) => onChange({ sale_customer_phone: e.target.value })}
                   className="luxe-input w-full px-2 py-1.5 text-xs"
                   placeholder="—" />
          </div>
          <div className="col-span-12 md:col-span-2">
            <div className="overline text-[0.5rem] mb-0.5">Payment</div>
            <select value={item.sale_payment_method}
                    onChange={(e) => onChange({ sale_payment_method: e.target.value })}
                    className="luxe-input w-full px-2 py-1.5 text-sm">
              <option>Cash</option>
              <option>Wire</option>
              <option>Card</option>
              <option>Crypto</option>
            </select>
          </div>
          <div className="col-span-12 text-[0.6rem] text-[#8A8A8A] mono">
            Total: Rp {new Intl.NumberFormat("id-ID").format(
              Math.round((Number(item.quantity_sold) || 0) * (Number(item.sale_price) || 0))
            )} · a sale invoice will be created on Approve Return.
          </div>
        </div>
      )}

      {isLost && (
        <div className="text-[0.65rem] text-red-300 bg-red-500/5 border border-red-400/20 px-2 py-1.5">
          This piece stays reserved and will appear on the Outstanding report.
          Notes optional:
          <input value={item.notes}
                 onChange={(e) => onChange({ notes: e.target.value })}
                 className="luxe-input w-full px-2 py-1 text-xs mt-1"
                 placeholder="reason / next step" />
        </div>
      )}
    </div>
  );
}

// =====================================================================
// DETAIL MODAL (read-only)
// =====================================================================

function DetailModal({ borrowing, onClose }) {
  return (
    <Modal onClose={onClose} title={`Borrowing · ${borrowing.staff_name}`}>
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Info label="Status"><StatusBadge status={borrowing.status} /></Info>
          <Info label="Date">{fmtDate(borrowing.borrow_date)}</Info>
          <Info label="Live Session">{borrowing.live_session || "—"}</Info>
          <Info label="Created by">{borrowing.created_by_name}</Info>
          <Info label="Issue approved by">{borrowing.issue_approved_by_name || <span className="text-[#666]">pending</span>}</Info>
          <Info label="Return submitted by">{borrowing.return_submitted_by_name || <span className="text-[#666]">—</span>}</Info>
          <Info label="Return approved by">{borrowing.return_approved_by_name || <span className="text-[#666]">—</span>}</Info>
          <Info label="Items">{borrowing.items.length}</Info>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[#8A8A8A]">
              <tr className="border-b border-white/10">
                <th className="text-left py-2 pr-3 mono uppercase text-[0.55rem]">SKU</th>
                <th className="text-left py-2 pr-3 mono uppercase text-[0.55rem]">Name</th>
                <th className="text-right py-2 pr-3 mono uppercase text-[0.55rem]">Qty</th>
                <th className="text-left py-2 pr-3 mono uppercase text-[0.55rem]">Outcome</th>
                <th className="text-left py-2 pr-3 mono uppercase text-[0.55rem]">Cond. After</th>
                <th className="text-left py-2 mono uppercase text-[0.55rem]">Notes</th>
              </tr>
            </thead>
            <tbody>
              {borrowing.items.map((it, i) => {
                const disp = it.disposition && it.disposition !== "pending"
                  ? it.disposition
                  : (it.returned ? "returned" : "pending");
                return (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 pr-3 mono text-[#D4AF37]">{it.sku}</td>
                    <td className="py-2 pr-3">{it.name}</td>
                    <td className="py-2 pr-3 text-right mono">{it.quantity}</td>
                    <td className="py-2 pr-3"><OutcomeCell item={it} disp={disp} /></td>
                    <td className="py-2 pr-3 text-[#8A8A8A]">{it.condition_after || "—"}</td>
                    <td className="py-2 text-[#8A8A8A]">{it.return_notes || it.notes || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

// =====================================================================
// SMALL SHARED UI
// =====================================================================

function OutcomeCell({ item, disp }) {
  const issued = Number(item.quantity || 0);
  if (disp === "returned") {
    const q = Number(item.quantity_returned || 0);
    return (
      <div>
        <span className="text-emerald-300 mono">{q} returned</span>
        {q + 1e-9 < issued && (
          <div className="text-[0.55rem] text-red-300">{issued - q} missing</div>
        )}
      </div>
    );
  }
  if (disp === "sold") {
    const q = Number(item.quantity_sold || 0);
    const total = q * Number(item.sale_price || 0);
    return (
      <div>
        <span className="text-[#D4AF37] mono">{q} sold</span>
        <div className="text-[0.55rem] text-[#8A8A8A]">
          Rp {new Intl.NumberFormat("id-ID").format(Math.round(total))}
          {item.sale_customer_name && ` · ${item.sale_customer_name}`}
        </div>
        {item.sale_invoice_number && (
          <div className="text-[0.55rem] mono text-[#8A8A8A]">
            {item.sale_invoice_number}
          </div>
        )}
      </div>
    );
  }
  if (disp === "lost") {
    return <span className="text-red-300 mono">missing</span>;
  }
  return <span className="text-[#666]">pending</span>;
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto glass-card p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="serif text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[#666] hover:text-white" data-testid="modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-[0.55rem] mb-1">{label}</div>
      {children}
    </label>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="overline text-[0.5rem] text-[#666]">{label}</div>
      <div className="text-[#EAEAEA] mt-0.5">{children}</div>
    </div>
  );
}
